import React, { useState } from 'react';
import 'animate.css';

function Contact() {
  const [formData, setFormData] = useState({
    firstname: '',
    email: '',
    telephone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [canSubmit, setCanSubmit] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Input filters ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;

    if (name === 'firstname') {
      // Only letters (including Slovak), spaces, hyphens
      filtered = value.replace(/[^a-zA-ZÀ-žáäčďéíľĺňóôŕšťúýžÁÄČĎÉÍĽĹŇÓÔŔŠŤÚÝŽ\s\-]/g, '');
    }
    if (name === 'telephone') {
      // Only digits, +, spaces, hyphens — max 13 chars
      filtered = value.replace(/[^\d+\s\-]/g, '').slice(0, 13);
    }

    setFormData((prev) => ({ ...prev, [name]: filtered }));
    // Clear error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // --- Validation on blur ---
  const validate = (name, value) => {
    switch (name) {
      case 'firstname':
        if (!value.trim()) return 'Meno je povinné';
        if (value.trim().length < 2) return 'Meno musí mať aspoň 2 znaky';
        return '';
      case 'email':
        if (!value.trim()) return 'Email je povinný';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Zadajte platný email';
        return '';
      case 'telephone':
        if (value && !/^[\d+][\d\s\-]{8,12}$/.test(value)) return 'Zadajte platné telefónne číslo';
        return '';
      case 'message':
        if (!value.trim()) return 'Správa je povinná';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validate(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleCheckbox = (e) => setCanSubmit(e.target.checked);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.entries(formData).forEach(([key, val]) => {
      const err = validate(key, val);
      if (err) newErrors[key] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const res = await fetch('https://formspree.io/f/mvgboqvl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _subject: 'Kontakt z webu – Petromin',
          _replyto: formData.email,
        }),
      });

      if (res.ok) {
        setStatus('ok');
        setFormData({ firstname: '', email: '', telephone: '', message: '' });
        setCanSubmit(false);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full border-b-2 outline-none p-3 transition-colors duration-200 ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-300 focus:border-red-600'
    }`;

  return (
    <div className="bg-white py-20 px-6">
      <div className="max-w-screen-xl mx-auto">
        <h2 className="text-center font-bold text-5xl text-gray-800 mb-4">Kontakt</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-14">
          Chcete získať bezplatnú cenovú ponuku? Neváhajte nás kontaktovať.
          Stačí vyplniť tento formulár a my sa postaráme o všetko ostatné.
        </p>

        <div className="flex flex-col lg:flex-row gap-12 justify-center items-start">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1305.9504909034154!2d21.505501604674382!3d49.107521521468314!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473e97030911bc1b%3A0xb3a5606c0042e89b!2sPETROMIN%2C%20spol.%20s.r.o.!5e0!3m2!1ssk!2ssk!4v1717443646955!5m2!1ssk!2ssk"
            width="100%"
            height="480"
            style={{ border: 0, minWidth: 280, maxWidth: 560 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="rounded-xl shadow-2xl flex-1"
            title="Mapa Petromin"
          ></iframe>

          <form className="flex-1 w-full max-w-md space-y-5" onSubmit={handleSubmit}>
            {/* Meno */}
            <div>
              <input
                type="text"
                name="firstname"
                placeholder="Vaše meno a priezvisko.."
                className={inputClass('firstname')}
                value={formData.firstname}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.firstname && <p className="text-red-500 text-xs mt-1">{errors.firstname}</p>}
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Váš email.."
                className={inputClass('email')}
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Telefón */}
            <div>
              <input
                type="tel"
                name="telephone"
                placeholder="Vaše telefónne číslo.. (napr. 0918 495 001)"
                className={inputClass('telephone')}
                value={formData.telephone}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.telephone && <p className="text-red-500 text-xs mt-1">{errors.telephone}</p>}
            </div>

            {/* Správa */}
            <div>
              <textarea
                name="message"
                placeholder="Správa.."
                rows={4}
                className={inputClass('message')}
                value={formData.message}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
            </div>

            {/* GDPR */}
            <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 accent-red-600"
                checked={canSubmit}
                onChange={handleCheckbox}
                required
              />
              <span>
                Oboznámil(a) som sa s podmienkami ochrany osobných údajov a súhlasím so spracovaním údajov uvedených vo formulári.
              </span>
            </label>

            {status === 'ok' && (
              <p className="text-green-600 font-semibold">Správa bola úspešne odoslaná!</p>
            )}
            {status === 'error' && (
              <p className="text-red-600 font-semibold">Nastala chyba. Skúste to znova.</p>
            )}

            <button
              type="submit"
              disabled={!canSubmit || loading}
              className={`w-full py-4 rounded font-semibold text-white transition-colors duration-200 ${
                canSubmit && !loading
                  ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? 'Odosielam...' : 'Odošli podnet'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contact;
